import React from 'react';
import { render } from 'react-dom';

import jsonGet from '../../util/jsonGet';
import jsonPost from '../../util/jsonPost';

import {
  RedirectModulePathName,
  createLogData,
  logToCAS,
  logToCASSuccess,
} from '../../config/helpers';
import { LogLevel } from '../../util/logConsts';
import { UserJourney, getUserJourney } from '../../util/userJourney';
import ErrorMessage from '../react/Core/ErrorMessage';
import Loading from '../react/Core/Loading';
import ProgressBar from '../react/ProgressBar';

export default class Redirect {
  constructor(context) {
    this.context = context;
    this.init();
  }

  // TODO: There's no redirecting if error currently, the error message shown at parts
  // is more internal-facing errors that won't make much sense to user.
  init() {
    const {
      url: redirectSubmitUrl,
      loadApi: loadApiUrlFromDataset,
      currentProgressBarStep,
      message,
      title,
      userJourney,
    } = this.context.dataset;
    const urlSearchParams = new URLSearchParams(window.location.search);
    const transactionKey =
      urlSearchParams.get('TransactionKey') || urlSearchParams.get('transactionKey');
    const processLoadApiUrlWithtransactionKey = `${loadApiUrlFromDataset}/${
      transactionKey == null ? '' : transactionKey
    }`;
    const orderId = urlSearchParams.get('OrderId') || urlSearchParams.get('orderId');
    const stripeSessionId =
      urlSearchParams.get('StripeSessionId') || urlSearchParams.get('stripeSessionId');
    const stripeInvoiceId =
      urlSearchParams.get('StripeInvoiceId') || urlSearchParams.get('stripeInvoiceId');

    // ERROR HANDLING
    // 1. CHECK ESSENTIAL INPUTS ARE PRESENT AND CORRECT
    const baseLogData = {
      redirectSubmitUrl,
      loadApiUrlFromDataset,
      timestamp: new Date().toISOString(),
      userAgent: navigator.userAgent,
      referrer: document.referrer,
      orderId,
      processLoadApiUrlWithtransactionKey,
      stripeSessionId,
      stripeInvoiceId,
      transactionKey,
      userJourney,
    };

    // Handle and log error if the redirectSubmitUrl is missing
    if (!redirectSubmitUrl) {
      const fullLogData = createLogData({
        logLevel: LogLevel.ERROR,
        moduleName: RedirectModulePathName,
        action: 'NoRedirectSubmitUrl',
        baseBody: 'redirectSubmitUrl is missing',
        additionalData: baseLogData,
      });
      logToCAS(fullLogData);
      throw Error('Missing redirectSubmitUrl');
    }

    // Handle and log error if the redirectSubmitUrl is malformed
    try {
      new URL(redirectSubmitUrl, window.location.origin); // Throws a TypeError if the URL is invalid
    } catch (error) {
      const fullLogData = createLogData({
        logLevel: LogLevel.ERROR,
        moduleName: RedirectModulePathName,
        action: 'MalformedRedirectSubmitUrl',
        baseBody: 'redirectSubmitUrl is malformed',
        additionalData: {
          ...baseLogData,
          errorMessage: error?.message,
          errorStack: error?.stack,
        },
      });
      logToCAS(fullLogData);
      throw Error('MalformedRedirectSubmitUrl');
    }

    if (getUserJourney(userJourney) !== UserJourney.StandalonePayment) {
      if (!loadApiUrlFromDataset) {
        const fullLogData = createLogData({
          logLevel: LogLevel.ERROR,
          moduleName: RedirectModulePathName,
          action: 'NoLoadApiUrlFromDataset',
          baseBody: 'loadApiUrlFromDataset is missing',
          additionalData: baseLogData,
        });
        logToCAS(fullLogData);
      }

      // Log error if the loadApi is missing
      try {
        new URL(loadApiUrlFromDataset, window.location.origin); // Throws a TypeError if the URL is invalid
      } catch (error) {
        const fullLogData = createLogData({
          logLevel: LogLevel.ERROR,
          moduleName: RedirectModulePathName,
          action: 'MalformedLoadApiUrlFromDataset',
          baseBody: 'LoadApiUrlFromDataset is malformed',
          additionalData: {
            ...baseLogData,
            errorMessage: error?.message,
            errorStack: error?.stack,
          },
        });
        logToCAS(fullLogData);
      }

      // 2. ERROR HANDLING - GET LOADING DATA & HANDLE ERROR/LOGGING
      jsonGet(
        processLoadApiUrlWithtransactionKey,
        null,
        (response) => {
          if (response.errorCode) {
            return render(<ErrorMessage text={response.message} />, this.context);
          }
          // 4. ERROR HANDLING - CATCH ON 'SUCCESSFUL' GET RESPONSE FAILURE
          Redirect.redirectToUrl(baseLogData, { loadApiResponse: response });

          return render(
            Redirect.renderMain(response, message, title, currentProgressBarStep),
            this.context
          );
        },
        true,
        (error) => {
          // 3. ERROR HANDLING - CATCH FETCHING THE LOAD API RESPONSE
          const fullLogData = createLogData({
            logLevel: LogLevel.CRITICAL,
            moduleName: RedirectModulePathName,
            action: 'LoadApiCallFailure',
            baseBody: `Failed to get data from ${processLoadApiUrlWithtransactionKey}.`,
            additionalData: {
              ...baseLogData,
              loadApiUrl: processLoadApiUrlWithtransactionKey,
              errorMessage: error?.message || 'Failed to fetch data',
              errorStack: error?.errorStack,
              errorCode: error?.errorCode,
              errorStatus: error?.errorStatus,
              url: error?.url,
              type: error?.type,
            },
          });
          logToCAS(fullLogData);
        }
      );
    } else {
      Redirect.redirectToUrl(baseLogData);

      return render(
        Redirect.renderMain(baseLogData, message, title, currentProgressBarStep),
        this.context
      );
    }

    return render(
      Redirect.renderMain(undefined, message, title, currentProgressBarStep),
      this.context
    );
  }

  static renderMain(data, message, title, selectedStep) {
    return (
      <>
        {selectedStep != null && (
          <ProgressBar steps={data?.progressBarSteps} selectedStep={selectedStep} />
        )}
        {title != null && (
          <div className="PageTitle">
            <h1 className="PageTitle_title">{title}</h1>
          </div>
        )}
        <div className="Redirect">
          <div
            className="Redirect_message"
            // eslint-disable-next-line react/no-danger
            dangerouslySetInnerHTML={{ __html: message }}
          />
          <Loading />
        </div>
      </>
    );
  }

  static handleSuccessfulSubmitApiRedirect(redirectUri, redirectLogPostSuccessData) {
    logToCASSuccess(redirectLogPostSuccessData).then(() => {
      window.top.location.href = redirectUri;
    });
  }

  static redirectToUrl(baseLogData, logContext = {}) {
    const { loadApiResponse } = logContext;
    const logLevelDependentOnLoadingApi =
      loadApiResponse && loadApiResponse?.errorCode ? LogLevel.CRITICAL : LogLevel.INFORMATION;
    const {
      redirectSubmitUrl,
      processLoadApiUrlWithtransactionKey,
      orderId,
      stripeSessionId,
      stripeInvoiceId,
    } = baseLogData;

    const redirectLogDataPreRedirect = createLogData({
      logLevel: logLevelDependentOnLoadingApi,
      moduleName: RedirectModulePathName,
      action: 'RedirectAttempt',
      baseBody:
        loadApiResponse && loadApiResponse?.errorCode
          ? `Critical error encountered in the GET request before redirect from
          ${processLoadApiUrlWithtransactionKey}
          to ${redirectSubmitUrl}:
          ${loadApiResponse?.errorMessage}`
          : `Preparing to redirect to ${redirectSubmitUrl}`,
      additionalData: {
        ...baseLogData,
        ...(loadApiResponse
          ? {
              loadResponseRedirectUri: loadApiResponse?.redirectUri,
              errorCode: loadApiResponse?.errorCode,
              errorMessage: loadApiResponse?.message || 'Failed to fetch data',
            }
          : {}),
      },
    });

    // 4.A. ERROR HANDLING - PRE RE-DIRECT CRITICAL ERROR OR PROVIDE INFO
    logToCAS(redirectLogDataPreRedirect).then(() => {
      const params = new URLSearchParams();

      if (orderId !== null) {
        params.set('orderId', orderId);
      }

      // One of stripeSessionId or stripeInvoiceId must be present
      if (stripeSessionId !== null) {
        params.set('stripeSessionId', stripeSessionId);
      }

      if (stripeInvoiceId !== null) {
        params.set('stripeInvoiceId', stripeInvoiceId);
      }

      const queryString = params.toString();
      const submitApiUrl = `${redirectSubmitUrl}?${queryString}`;

      // Post to the redirect API and log error response if needed
      const pollSubmitApi = () => {
        jsonPost(
          submitApiUrl,
          null,
          (submitApiResponse) => {
            // If no error, log success and perform the redirect when redirectUri is available
            if (!submitApiResponse.error && submitApiResponse.redirectUri) {
              const redirectLogPostSuccessData = createLogData({
                logLevel: LogLevel.INFORMATION,
                moduleName: RedirectModulePathName,
                action: 'SubmitApiPostCompletionWithRedirect',
                baseBody: `Successful jsonPost to submitApiUrl: ${submitApiUrl}. Payment and redirect completed successfully.`,
                additionalData: {
                  ...baseLogData,
                  submitApiUrl,
                },
              });
              Redirect.handleSuccessfulSubmitApiRedirect(
                submitApiResponse.redirectUri,
                redirectLogPostSuccessData
              );
              return;
            }

            if (!submitApiResponse.error && submitApiResponse.processing) {
              setTimeout(pollSubmitApi, 10000);
              return;
            }

            // / 4.B. ERROR HANDLING If 'successful' but an error occurred, log the error
            const redirectFailData = createLogData({
              logLevel: LogLevel.ERROR,
              moduleName: RedirectModulePathName,
              action: 'RedirectErrorInPostRequest',
              baseBody: `Error during POST request redirect to submit api: ${submitApiUrl}`,
              additionalData: {
                ...baseLogData,
                submitApiUrl,
                ...(submitApiResponse?.errorCode
                  ? { errorCode: submitApiResponse?.errorCode }
                  : {}),
                ...(submitApiResponse?.errorMessage
                  ? { errorMessage: submitApiResponse?.errorMessage }
                  : {}),
                ...(submitApiResponse?.redirectUri
                  ? { redirectUri: submitApiResponse?.redirectUri }
                  : {}),
              },
            });
            logToCAS(redirectFailData);
          },
          true,
          (error) => {
            // 4.C. ERROR HANDLING If an exception occurs during the redirect,
            // log it as a critical error
            const redirectFailData = createLogData({
              logLevel: LogLevel.CRITICAL,
              moduleName: RedirectModulePathName,
              action: 'RedirectException',
              baseBody: `Exception occurred during redirect to ${submitApiUrl}`,
              additionalData: {
                ...baseLogData,
                submitApiUrl,
                ...(error?.exception ? { exception: error.toString() } : {}),
                ...(error?.errorCode ? { errorCode: error?.errorCode } : {}),
                ...(error?.errorMessage ? { errorMessage: error?.errorMessage } : {}),
                ...(error?.errorStack ? { errorStack: error?.errorStack } : {}),
              },
            });
            logToCAS(redirectFailData);
          }
        );
      };

      pollSubmitApi();
    });
  }
}
